// ---------------------------------------------------------------------------
// PWA Caisse Tablette — Reservation Sync Service
// Manages a separate IndexedDB database (CaisseDB v2) for reservation
// caching and provides search across the local index.
// ---------------------------------------------------------------------------

import { apiClient } from '@/services/api-client';
import { useReservationStore } from '@/store/reservation-store';
import { SYNC_INTERVAL } from '@/lib/constants';
import type { Reservation } from '@/lib/types';

// ---------------------------------------------------------------------------
// Constants -- separate DB from offline queue
// ---------------------------------------------------------------------------

const RESA_DB_NAME = 'CaisseDB';
const RESA_DB_VERSION = 2;
const STORE_RESERVATIONS = 'reservations';
const STORE_METADATA = 'metadata';

// ---------------------------------------------------------------------------
// Reservation Sync
// ---------------------------------------------------------------------------

class ReservationSyncService {
  private db: IDBDatabase | null = null;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private searchIndex = new Map<string, Reservation[]>();

  // ---- initialization -----------------------------------------------------

  /**
   * Open the CaisseDB IndexedDB database.
   */
  async initDatabase(): Promise<void> {
    if (this.db) return;

    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(RESA_DB_NAME, RESA_DB_VERSION);

      request.onerror = () => {
        console.error('[ReservationSync] IndexedDB open error');
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('[ReservationSync] IndexedDB opened');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_RESERVATIONS)) {
          const store = db.createObjectStore(STORE_RESERVATIONS, {
            keyPath: 'id',
          });
          store.createIndex('client_name', 'client_name', { unique: false });
          store.createIndex('client_phone', 'client_phone', { unique: false });
          store.createIndex('table_id', 'table_id', { unique: false });
          store.createIndex('date', 'date', { unique: false });
          store.createIndex('status', 'status', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORE_METADATA)) {
          db.createObjectStore(STORE_METADATA, { keyPath: 'key' });
        }
      };
    });
  }

  // ---- cache loading ------------------------------------------------------

  /**
   * Load reservations from the IndexedDB cache into the Zustand store.
   */
  async loadFromCache(): Promise<Reservation[]> {
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_RESERVATIONS], 'readonly');
      const store = tx.objectStore(STORE_RESERVATIONS);
      const request = store.getAll();

      request.onsuccess = () => {
        const reservations = (request.result as Reservation[]) ?? [];
        this.buildSearchIndex(reservations);
        useReservationStore.getState().setReservations(reservations);
        console.log(
          `[ReservationSync] ${reservations.length} reservations loaded from cache`,
        );
        resolve(reservations);
      };

      request.onerror = () => reject(request.error);
    });
  }

  // ---- sync ---------------------------------------------------------------

  /**
   * Sync reservations from the backend API into local cache and Zustand store.
   */
  async sync(): Promise<void> {
    try {
      await this.ensureDb();

      console.log('[ReservationSync] Syncing...');
      const response = await apiClient.getReservations();
      const serverReservations = response.reservations ?? [];

      // Update IDB cache
      await this.updateCache(serverReservations);

      // Update memory + Zustand store
      this.buildSearchIndex(serverReservations);
      useReservationStore.getState().setReservations(serverReservations);

      console.log(
        `[ReservationSync] ${serverReservations.length} reservations synced`,
      );
    } catch (error) {
      console.error('[ReservationSync] Sync error:', (error as Error).message);
    }
  }

  /**
   * Update the IndexedDB cache with fresh reservations.
   */
  private async updateCache(reservations: Reservation[]): Promise<void> {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction([STORE_RESERVATIONS], 'readwrite');
      const store = tx.objectStore(STORE_RESERVATIONS);

      // Clear old data
      store.clear();

      // Insert new data
      for (const reservation of reservations) {
        store.add(reservation);
      }

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // ---- search index -------------------------------------------------------

  /**
   * Build an in-memory search index for fast local lookups.
   */
  private buildSearchIndex(reservations: Reservation[]): void {
    this.searchIndex.clear();

    for (const reservation of reservations) {
      // Index by name (lowercase)
      const name = (reservation.client_name ?? '').toLowerCase();
      if (name) {
        if (!this.searchIndex.has(name)) {
          this.searchIndex.set(name, []);
        }
        this.searchIndex.get(name)!.push(reservation);
      }

      // Index by phone
      const phone = reservation.client_phone ?? '';
      if (phone) {
        if (!this.searchIndex.has(phone)) {
          this.searchIndex.set(phone, []);
        }
        this.searchIndex.get(phone)!.push(reservation);
      }

      // Index by QR code
      const qr = reservation.qr_code ?? '';
      if (qr) {
        this.searchIndex.set(qr, [reservation]);
      }

      // Index by ID
      const id = String(reservation.id ?? '');
      if (id) {
        this.searchIndex.set(id, [reservation]);
      }
    }
  }

  // ---- search -------------------------------------------------------------

  /**
   * Search reservations locally by name, phone, QR code, or ID.
   */
  search(query: string): Reservation[] {
    if (!query) return [];

    const queryLower = query.toLowerCase();
    const results = new Set<Reservation>();

    // Exact match in index
    const exactMatch = this.searchIndex.get(query);
    if (exactMatch) {
      exactMatch.forEach((r) => results.add(r));
    }

    const lowerMatch = this.searchIndex.get(queryLower);
    if (lowerMatch) {
      lowerMatch.forEach((r) => results.add(r));
    }

    // Partial match in index keys
    for (const [key, reservations] of this.searchIndex.entries()) {
      if (key.includes(queryLower)) {
        reservations.forEach((r) => results.add(r));
      }
    }

    // Full-text search across all reservations
    const allReservations = useReservationStore.getState().reservations;
    for (const reservation of allReservations) {
      if (this.matchesQuery(reservation, queryLower)) {
        results.add(reservation);
      }
    }

    return Array.from(results);
  }

  /**
   * Get reservations assigned to a specific table.
   */
  getByTable(tableId: string): Reservation[] {
    const allReservations = useReservationStore.getState().reservations;
    return allReservations.filter((r) => r.table_id === tableId);
  }

  private matchesQuery(reservation: Reservation, query: string): boolean {
    const fields: (string | undefined)[] = [
      reservation.client_name,
      reservation.client_phone,
      reservation.qr_code,
      String(reservation.id),
      reservation.table_id,
    ];

    return fields.some((field) => {
      if (!field) return false;
      return field.toLowerCase().includes(query);
    });
  }

  // ---- auto-sync ----------------------------------------------------------

  /**
   * Start periodic reservation sync.
   */
  startAutoSync(): void {
    this.stopAutoSync();

    this.syncInterval = setInterval(() => {
      this.sync().catch(() => {});
    }, SYNC_INTERVAL);

    console.log('[ReservationSync] Auto-sync started');
  }

  /**
   * Stop periodic reservation sync.
   */
  stopAutoSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  // ---- internal helpers ---------------------------------------------------

  private async ensureDb(): Promise<void> {
    if (!this.db) await this.initDatabase();
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const reservationSync = new ReservationSyncService();
